import React, { useEffect, useRef, useState } from 'react';
import { Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { renderToStaticMarkup } from 'react-dom/server';
import 'leaflet.markercluster';
import 'leaflet.markercluster.placementstrategies';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import './GeoJsonDatexPOIMap.scss';
import { Divider } from 'antd';
import { getIcon } from '@bvt-shared/utils/iconUtils';
import { PopupStartSipro } from '@bvt-shared/component/PopupStartSipro';
import { featureFlags } from '@bvt-utils';
import { useAuth } from '@bvt-features/auth/hooks';

function formatToRupiah(value) {
  const num =
    typeof value === 'number' ? value : parseFloat(value);

  if (isNaN(num)) {
    return value;
  }
  if (num === 0) {
    return 'Rp 0/kWh';
  }

  return `${new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
  }).format(num)}/kWh`;
}

const Content = ({ properties, onClose }) => {
  const AUTH = useAuth();
  const isPertamina = AUTH.hasAccess('MAPANA.ASMANPERTAMINA');
  const chargingStationID = process.env.CHARGING_STATION_PERTAMINA_ID || '371';
  const formatFee = (fee) => {
    return fee
      .split(',')
      .map((v) => {
        const fee = ['Free', null].includes(v.trim()) ? '0' : v;
        return `${formatToRupiah(fee)}`;
      })
      .join(', ');
  };

  const formatVoltage = (voltage) => {
    return voltage?.replace(/\b(\d+(\.\d+)?)\s*Kw\b/g, '$1kW');
  };

  return (
    <PopupStartSipro marker={properties} onClose={onClose}>
      <div className='DatexPOIMap__popup'>
        <div className='header'>
          <div className='icon'>
            <img
              alt=''
              src={getIcon('poi', properties?.category_icon)}
              style={{ height: '2.25rem', width: '2.25rem' }}
            />
          </div>
          <div className='wrapper-title'>
            <div className='title'>{properties?.poi_name}</div>
            <div className='subtitle'>{properties.industry_name}</div>
          </div>
        </div>
        <div className='body'>
          <div className='detail'>
            <div className='detail--text'>Group</div>
            <div className='detail--text'>{properties?.group_name}</div>
          </div>
          <div className='detail'>
            <div className='detail--text'>Category</div>
            <div className='detail--text'>{properties?.category_name}</div>
          </div>
          {featureFlags.pertamina.isPhase2Enabled &&
          properties?.category_id === +chargingStationID &&
          isPertamina ? (
              <div className='detail'>
                <div className='detail--text'>Brand</div>
                <div className='detail--text'>{properties?.brand_name}</div>
              </div>
            ) : null}
          <Divider className='divider' />
          {featureFlags.pertamina.isPhase2Enabled &&
          properties?.category_id === +chargingStationID &&
          isPertamina ? (
              <>
                <div className='detail'>
                  <div className='detail--text'>Type</div>
                  <div className='detail--text'>{properties?.type || 'N/A'}</div>
                </div>
                <div className='detail'>
                  <div className='detail--text'>Total</div>
                  <div className='detail--text'>{properties?.total || 'N/A'}</div>
                </div>
                <div className='detail'>
                  <div className='detail--text'>Fee</div>
                  <div className='detail--text'>
                    {properties?.fee ? formatFee(properties?.fee) : 'N/A'}
                  </div>
                </div>
                <div className='detail'>
                  <div className='detail--text'>Type of Current</div>
                  <div className='detail--text'>
                    {properties?.type_of_current || 'N/A'}
                  </div>
                </div>
                <div className='detail'>
                  <div className='detail--text'>Voltage</div>
                  <div className='detail--text'>
                    {formatVoltage(properties?.voltage) || 'N/A'}
                  </div>
                </div>
                <div className='detail'>
                  <div className='detail--text'>Fast Charger</div>
                  <div className='detail--text'>
                    {properties?.fast_charger || 'N/A'}
                  </div>
                </div>
                <Divider className='divider' />
              </>
            ) : null}
          <div className='detail'>
            <div className='detail--text'>Longitude</div>
            <div className='detail--text'>{properties?.longitude}</div>
          </div>
          <div className='detail'>
            <div className='detail--text'>Latitude</div>
            <div className='detail--text'>{properties?.latitude}</div>
          </div>
          <div className='detail'>
            <div className='detail--text'>Address</div>
            <div className='detail--text'>
              {properties?.address?.length > 23
                ? properties?.address?.substring(0, 20) + '...'
                : properties?.address}
            </div>
          </div>
        </div>
      </div>
    </PopupStartSipro>
  );
};

function PopupContent(props) {
  const { lat, lng, properties, onClose } = props;

  if (lat && lng) {
    const commonPopupProps = {
      className: 'MarkerPopup-li__popup',
      offset: { x: 12, y: -16 },
      position: { lat, lng },
    };

    return (
      <Popup {...commonPopupProps} onClose={onClose}>
        <div className={`MarkerPopup-li__popup-wrap`}>
          <Content onClose={onClose} properties={properties} />
        </div>
      </Popup>
    );
  }

  return null;
}

function areEqual(prevProps, nextProps) {
  return (
    prevProps.lat === nextProps.lat &&
    prevProps.lng === nextProps.lng &&
    JSON.stringify(prevProps.properties) ===
      JSON.stringify(nextProps.properties)
  );
}

const PopupContentMemoized = React.memo(PopupContent, areEqual);

/**
 * @copyright PT Bhumi Varta Technology
 * @author Malik <malik.ibrahim@bvarta.com>
 * @description Geojson for datex POI
 * @param {Object} props.data
 * @param {Number} props.opacity
 */
export const GeoJsonDatexPOIMap = (props) => {
  const map = useMap();
  const activeMarkerRef = useRef(null);
  const [popupPosition, setPopupPosition] = useState({
    lat: null,
    lng: null,
    properties: {},
  });

  const handleClosePopup = () => {
    const tip = document.querySelector('.leaflet-popup-tip-container');
    const closeBtn = document.querySelector('.leaflet-popup-close-button');
    if (tip && closeBtn) {
      tip.style.display = 'none';
      closeBtn.style.display = 'none';
    }
    setPopupPosition({
      lat: null,
      lng: null,
      properties: {},
    });
    activeMarkerRef.current = null;
  };

  useEffect(() => {
    let markerClusterGroupInstance = new L.MarkerClusterGroup({
      showCoverageOnHover: false,
      spiderfyDistanceMultiplier: 1.2,
      maxClusterRadius: 80,
      iconCreateFunction: (icfParam) => {
        const totalMarkerPerCluster = icfParam.getChildCount();

        const multiplier = map.getZoom() > 16 ? 8 : 3;
        const percentageOfTotalMarker =
          (totalMarkerPerCluster / props?.data?.length || 1) * 100;
        const opacityMultiplier = (percentageOfTotalMarker / 10) * multiplier;

        const sizeMultiPlier = opacityMultiplier > 0.9 ? 1.3 : 1.1;

        const baseStyle = {
          background: `rgba(151,106,218,${props.opacity / 2 || 0.5})`,
          width: 42 * sizeMultiPlier,
          height: 42 * sizeMultiPlier,
          borderRadius: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        };
        const htmlElement = renderToStaticMarkup(
          <div style={baseStyle}>
            <div
              style={{
                ...baseStyle,
                opacity: isNaN(props?.opacity) ? 1 : props?.opacity,
                width: 32 * sizeMultiPlier,
                height: 32 * sizeMultiPlier,
                color: '#DEEEED',
                fontWeight: '400',
                fontSize: '16px',
                lineHeight: '26px',
              }}
            >
              {totalMarkerPerCluster}
            </div>
          </div>
        );

        return L.divIcon({ html: htmlElement, iconAnchor: { x: 22, y: 22 } });
      },
    });

    if (props?.data?.length > 0) {
      // Add Cluster To Map

      props.data?.forEach((el) => {
        const properties = el?.properties;
        let markerLayer = L.marker(el.position, {
          icon: new L.DivIcon({
            html: renderToStaticMarkup(
              <div
                className=''
                data-meta={JSON.stringify(el)}
                style={{ opacity: isNaN(props.opacity) ? 1 : props.opacity }}
              >
                <img
                  alt=''
                  src={getIcon('poi', properties?.category_icon)}
                  style={{ height: '2.25rem', width: '2.25rem' }}
                />
              </div>
            ),
          }),
        });

        markerLayer.on('click', (e) => {
          const tip = document.querySelector('.leaflet-popup-tip-container');
          const closeBtn = document.querySelector(
            '.leaflet-popup-close-button'
          );
          if (tip && closeBtn) {
            tip.style.display = 'flex';
            closeBtn.style.display = 'flex';
          }
          activeMarkerRef.current = markerLayer;
          setPopupPosition({
            lat: e.latlng.lat,
            lng: e.latlng.lng,
            properties: {
              ...el.properties,
            },
          });
        });

        // Add Marker To cluster Layer
        markerClusterGroupInstance.addLayer(markerLayer);
      });

      markerClusterGroupInstance.addTo(map);
    }

    map.on('zoomend', () => {
      const activeMarker = activeMarkerRef.current;
      if (activeMarker && !map.hasLayer(activeMarker)) {
        handleClosePopup();
      }
    });

    return () => {
      // Remove Cluster On Remount
      markerClusterGroupInstance.removeFrom(map);
    };
  }, [props.data, props.opacity]);

  return (
    <PopupContentMemoized
      lat={popupPosition.lat}
      lng={popupPosition.lng}
      onClose={handleClosePopup}
      properties={popupPosition.properties}
    />
  );
};
